import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../apiClient";
import { dateTime, plainWords } from "../customerFormat";

const toRows = (value) => (Array.isArray(value) ? value : []);

const clean = (value) => String(value || "").trim();


export default function FilesTab({ record }) {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");

  const ownerParams = useMemo(() => {
    if (record?.object === "customer" && record?.raw?.id)
      return { customer: record.raw.id };
    if (record?.object === "lead" && (record?.raw?.id || record?.id))
      return { lead: record.raw?.id || record.id };
    return null;
  }, [record]);

  const loadFiles = useCallback(async () => {
    if (!ownerParams) {
      setFiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const rows = await api
      .get("/storage/files/", { params: ownerParams })
      .then((res) => toRows(res.data))
      .catch(() => []);
    setFiles(rows);
    setLoading(false);
  }, [ownerParams]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const upload = async () => {
    if (!selectedFile) return;
    const form = new FormData();
    form.append("file", selectedFile);
    form.append("category", "document");
    if (ownerParams?.customer) form.append("customer", String(ownerParams.customer));

    setUploading(true);
    setError("");
    try {
      await api.post("/storage/files/upload/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelectedFile(null);
      await loadFiles();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      setError(`Upload failed${detail ? `: ${detail}` : ""}. The file was not saved.`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ct-stack">
      <section className="cov-card">
        <div className="cov-card__head">
          <span>Documents</span>
          <span className="cov-card__count">{loading ? "…" : files.length}</span>
        </div>
        <div className="cov-card__body">
          {loading ? <div className="cov-empty">Loading documents…</div> : null}
          {!loading && !files.length ? <div className="cov-empty">No documents uploaded for this customer.</div> : null}
          {!loading &&
            files.map((file) => (
              <div key={file.file_uuid || file.id} className="cov-row">
                <div className="cov-row__main">
                  <span className="cov-row__title">{clean(file.name) || "File"}</span>
                  <span className="cov-row__meta">
                    {[plainWords(file.category) || "Document", dateTime(file.created_at)].filter(Boolean).join(" · ")}
                  </span>
                </div>
                {file.url ? (
                  <a className="ct-more" href={file.url} target="_blank" rel="noreferrer">
                    Open
                  </a>
                ) : (
                  <span className="cov-pill cov-pill--muted">{plainWords(file.upload_status) || "Processing"}</span>
                )}
              </div>
            ))}
        </div>
      </section>

      {ownerParams ? (
        <section className="cov-card">
          <div className="cov-card__head">
            <span>Upload a document</span>
          </div>
          <div className="cov-card__body">
            <input type="file" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
            <button type="button" className="ct-more" disabled={!selectedFile || uploading} onClick={upload}>
              {uploading ? "Uploading…" : "Upload"}
            </button>
            {error ? <div className="cov-fact cov-fact--danger">{error}</div> : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}
